import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import LeadForm from '../components/LeadForm';
import {
  Phone,
  CheckCircle,
  Leaf,
  TreeDeciduous,
  Palette,
  Droplets,
  Wind,
  Scissors,
  FileText,
  Wrench,
  Award,
  Users,
  ShieldCheck,
  Clock,
  MessageSquare,
  ArrowRight,
} from 'lucide-react';

const HomePage = () => {
  const services = [
    {
      icon: Leaf,
      title: 'Lawn Installation & Maintenance',
      description: 'Professional lawn installation and ongoing maintenance to keep your grass healthy and vibrant.',
      image: 'https://images.pexels.com/photos/6728919/pexels-photo-6728919.jpeg?auto=compress&cs=tinysrgb&w=800',
    },
    {
      icon: Palette,
      title: 'Landscape Design',
      description: 'Custom landscape designs tailored to your property and the Pacific Northwest climate.',
      image: 'https://images.unsplash.com/photo-1558904541-efa843a96f01?q=80&w=800&auto=format&fit=crop',
    },
    {
      icon: TreeDeciduous,
      title: 'Patio & Hardscape Installation',
      description: 'Beautiful patios, walkways, and retaining walls built with quality materials.',
      image: 'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?q=80&w=800&auto=format&fit=crop',
    },
    {
      icon: Droplets,
      title: 'Irrigation Systems',
      description: 'Efficient irrigation solutions to keep your landscape properly watered year-round.',
      image: 'https://images.unsplash.com/photo-1416879595882-3373a0480b5b?q=80&w=800&auto=format&fit=crop',
    },
    {
      icon: Wind,
      title: 'Seasonal Yard Cleanup',
      description: 'Thorough seasonal cleanup services including leaf removal and debris clearing.',
      image: 'https://images.unsplash.com/photo-1509423350716-97f9360b4e09?q=80&w=800&auto=format&fit=crop',
    },
    {
      icon: Scissors,
      title: 'Tree & Shrub Trimming',
      description: 'Expert pruning and trimming to maintain the health and appearance of your plants.',
      image: 'https://images.unsplash.com/photo-1598902108854-10e335adac99?q=80&w=800&auto=format&fit=crop',
    },
  ];

  const processSteps = [
    {
      step: 1,
      icon: MessageSquare,
      title: 'Schedule Consultation',
      description: 'Contact us to schedule a free on-site consultation at your convenience.',
    },
    {
      step: 2,
      icon: FileText,
      title: 'Receive Detailed Estimate',
      description: 'We provide a comprehensive written estimate with transparent pricing.',
    },
    {
      step: 3,
      icon: Wrench,
      title: 'Professional Installation',
      description: 'Our experienced crew completes your project with quality workmanship.',
    },
  ];

  const whyChooseUs = [
    {
      icon: Users,
      title: 'Locally Owned & Operated',
      description: 'Serving Seattle and surrounding areas with personalized service.',
    },
    {
      icon: FileText,
      title: 'Transparent Written Pricing',
      description: 'Detailed estimates with no hidden fees or surprise charges.',
    },
    {
      icon: Award,
      title: 'Experienced Crew',
      description: 'Skilled landscaping professionals with years of local experience.',
    },
    {
      icon: ShieldCheck,
      title: 'Quality Materials',
      description: 'We use only high-quality materials suited for the Pacific Northwest.',
    },
    {
      icon: Clock,
      title: 'Reliable Scheduling',
      description: 'We show up on time and complete projects as promised.',
    },
    {
      icon: MessageSquare,
      title: 'Dedicated Support',
      description: 'Responsive customer service throughout your project.',
    },
  ];

  const projects = [
    {
      image: 'https://images.unsplash.com/photo-1558244380-3d44c532012d?q=80&w=800&auto=format&fit=crop',
      title: 'Backyard Landscape Design',
      location: 'Seattle, WA',
    },
    {
      image: 'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?q=80&w=800&auto=format&fit=crop',
      title: 'Patio Installation',
      location: 'Bellevue, WA',
    },
    {
      image: 'https://images.unsplash.com/photo-1558904541-efa843a96f01?q=80&w=800&auto=format&fit=crop',
      title: 'Residential Lawn Installation',
      location: 'Kirkland, WA',
    },
  ];

  return (
    <div className="min-h-screen" data-testid="home-page">
      {/* Hero Section */}
      <section
        className="relative min-h-[90vh] flex items-center justify-center bg-cover bg-center"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1614447177156-7988dc2f4c2a?q=80&w=2000&auto=format&fit=crop)',
        }}
        data-testid="hero-section"
      >
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/40 to-black/30" />
        <div className="relative z-10 container mx-auto px-4 md:px-6 lg:px-8 text-center text-white">
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 font-['Poppins'] animate-fade-in-up">
            Professional Landscaping Services<br />
            <span className="text-[#D4E157]">in Seattle, WA</span>
          </h1>
          <p className="text-lg md:text-xl text-gray-200 max-w-2xl mx-auto mb-8 animate-fade-in-up animation-delay-100">
            Reliable residential landscaping solutions tailored to your property and budget. Transform your outdoor space with Seattle's trusted landscaping experts.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-10 animate-fade-in-up animation-delay-200">
            <Link to="/contact#estimate-form">
              <Button
                className="bg-[#2E5C31] hover:bg-[#244A26] text-white rounded-full px-8 py-6 text-lg font-semibold shadow-lg hover:shadow-xl transition-all duration-300"
                data-testid="hero-cta-estimate"
              >
                Request Free Estimate
              </Button>
            </Link>
            <Button
              asChild
              variant="outline"
              className="bg-white/10 backdrop-blur-sm border-white text-white hover:bg-white hover:text-[#2E5C31] rounded-full px-8 py-6 text-lg font-semibold transition-all duration-300"
              data-testid="hero-cta-call"
            >
              <a href="tel:+18888808889">
                <Phone className="mr-2 h-5 w-5" />
                Call Now
              </a>
            </Button>
          </div>
          
          {/* Trust Indicators */}
          <div className="flex flex-wrap justify-center gap-4 md:gap-6 animate-fade-in-up animation-delay-300">
            <div className="trust-badge bg-white/10 backdrop-blur-sm">
              <CheckCircle className="w-4 h-4 text-[#D4E157]" />
              <span>Experienced Landscaping Team</span>
            </div>
            <div className="trust-badge bg-white/10 backdrop-blur-sm">
              <CheckCircle className="w-4 h-4 text-[#D4E157]" />
              <span>Transparent Written Estimates</span>
            </div>
            <div className="trust-badge bg-white/10 backdrop-blur-sm">
              <CheckCircle className="w-4 h-4 text-[#D4E157]" />
              <span>Reliable Scheduling</span>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 md:py-28 bg-[#FAFAFA]" data-testid="services-section">
        <div className="container mx-auto px-4 md:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="inline-block text-[#2E5C31] font-semibold text-sm uppercase tracking-wider mb-3">Our Services</span>
            <h2 className="text-3xl md:text-4xl font-bold text-[#1A1A1A] mb-4 font-['Poppins']">
              Complete Landscaping Solutions
            </h2>
            <p className="text-[#4A4A4A] max-w-2xl mx-auto">
              From lawn care to complete landscape transformations, we offer comprehensive services to meet all your outdoor needs.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <div
                key={service.title}
                className="group bg-white rounded-2xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 border border-gray-100"
                data-testid={`service-card-${index}`}
              >
                <div className="relative h-48 overflow-hidden">
                  <img
                    src={service.image}
                    alt={service.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
                  <div className="absolute bottom-4 left-4 w-12 h-12 bg-[#2E5C31] rounded-full flex items-center justify-center">
                    <service.icon className="w-6 h-6 text-white" />
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-semibold text-[#1A1A1A] mb-2 font-['Poppins']">
                    {service.title}
                  </h3>
                  <p className="text-[#4A4A4A] text-sm leading-relaxed">
                    {service.description}
                  </p>
                </div>
              </div>
            ))}
          </div>

          <p className="text-center text-[#888888] text-sm mt-10 max-w-2xl mx-auto">
            Service pricing varies based on property size, materials, and project scope. Contact us for a detailed estimate.
          </p>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-20 md:py-28 bg-white" data-testid="process-section">
        <div className="container mx-auto px-4 md:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="inline-block text-[#2E5C31] font-semibold text-sm uppercase tracking-wider mb-3">How It Works</span>
            <h2 className="text-3xl md:text-4xl font-bold text-[#1A1A1A] mb-4 font-['Poppins']">
              Simple 3-Step Process
            </h2>
            <p className="text-[#4A4A4A] max-w-2xl mx-auto">
              We make getting your landscaping project started easy with clear communication every step of the way.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-4">
            {processSteps.map((item, index) => (
              <div key={item.step} className="relative" data-testid={`process-step-${index}`}>
                <div className="bg-[#F2F5F3] rounded-2xl p-8 text-center h-full">
                  <div className="w-16 h-16 bg-[#2E5C31] rounded-full flex items-center justify-center mx-auto mb-6">
                    <item.icon className="w-8 h-8 text-white" />
                  </div>
                  <div className="inline-block bg-[#D4E157] text-[#1A1A1A] font-bold text-sm px-4 py-1 rounded-full mb-4">
                    Step {item.step}
                  </div>
                  <h3 className="text-xl font-semibold text-[#1A1A1A] mb-3 font-['Poppins']">
                    {item.title}
                  </h3>
                  <p className="text-[#4A4A4A]">{item.description}</p>
                </div>
                {index < processSteps.length - 1 && (
                  <div className="hidden md:block absolute top-1/2 -right-2 transform -translate-y-1/2 z-10">
                    <ArrowRight className="w-8 h-8 text-[#2E5C31]" />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Recent Projects Section */}
      <section className="py-20 md:py-28 bg-[#FAFAFA]" data-testid="projects-section">
        <div className="container mx-auto px-4 md:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="inline-block text-[#2E5C31] font-semibold text-sm uppercase tracking-wider mb-3">Our Work</span>
            <h2 className="text-3xl md:text-4xl font-bold text-[#1A1A1A] mb-4 font-['Poppins']">
              Recent Projects
            </h2>
            <p className="text-[#4A4A4A] max-w-2xl mx-auto">
              Browse through some of our recent landscaping projects in the Seattle area.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {projects.map((project, index) => (
              <div
                key={index}
                className="group relative overflow-hidden rounded-2xl shadow-lg"
                data-testid={`project-card-${index}`}
              >
                <img
                  src={project.image}
                  alt={project.title}
                  className="w-full h-72 object-cover group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-6">
                  <h3 className="text-xl font-semibold text-white mb-1 font-['Poppins']">
                    {project.title}
                  </h3>
                  <p className="text-gray-300 text-sm">{project.location}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-10">
            <Link to="/projects">
              <Button
                variant="outline"
                className="border-[#2E5C31] text-[#2E5C31] hover:bg-[#2E5C31] hover:text-white rounded-full px-8 py-3 font-semibold transition-all duration-300"
                data-testid="view-all-projects-btn"
              >
                View All Projects
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-20 md:py-28 bg-white" data-testid="why-us-section">
        <div className="container mx-auto px-4 md:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="inline-block text-[#2E5C31] font-semibold text-sm uppercase tracking-wider mb-3">Why Choose Us</span>
            <h2 className="text-3xl md:text-4xl font-bold text-[#1A1A1A] mb-4 font-['Poppins']">
              The DecorVista Difference
            </h2>
            <p className="text-[#4A4A4A] max-w-2xl mx-auto">
              We're committed to providing exceptional landscaping services with transparency and professionalism.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {whyChooseUs.map((item, index) => (
              <div
                key={item.title}
                className="flex items-start gap-4 p-6 bg-[#F2F5F3] rounded-xl"
                data-testid={`why-us-card-${index}`}
              >
                <div className="w-12 h-12 bg-[#2E5C31] rounded-lg flex items-center justify-center flex-shrink-0">
                  <item.icon className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-[#1A1A1A] mb-2 font-['Poppins']">
                    {item.title}
                  </h3>
                  <p className="text-[#4A4A4A] text-sm">{item.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Transparency Section */}
      <section className="py-20 md:py-28 bg-[#2E5C31]" data-testid="pricing-section">
        <div className="container mx-auto px-4 md:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center text-white">
            <span className="inline-block text-[#D4E157] font-semibold text-sm uppercase tracking-wider mb-3">Pricing</span>
            <h2 className="text-3xl md:text-4xl font-bold mb-6 font-['Poppins']">
              Transparent Pricing Policy
            </h2>
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 md:p-12">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
                <div className="text-center">
                  <div className="w-16 h-16 bg-[#D4E157] rounded-full flex items-center justify-center mx-auto mb-4">
                    <FileText className="w-8 h-8 text-[#1A1A1A]" />
                  </div>
                  <h3 className="font-semibold mb-2">Detailed Estimates</h3>
                  <p className="text-gray-300 text-sm">
                    Comprehensive written estimates before any work begins
                  </p>
                </div>
                <div className="text-center">
                  <div className="w-16 h-16 bg-[#D4E157] rounded-full flex items-center justify-center mx-auto mb-4">
                    <ShieldCheck className="w-8 h-8 text-[#1A1A1A]" />
                  </div>
                  <h3 className="font-semibold mb-2">No Hidden Fees</h3>
                  <p className="text-gray-300 text-sm">
                    The price we quote is the price you pay - no surprises
                  </p>
                </div>
                <div className="text-center">
                  <div className="w-16 h-16 bg-[#D4E157] rounded-full flex items-center justify-center mx-auto mb-4">
                    <MessageSquare className="w-8 h-8 text-[#1A1A1A]" />
                  </div>
                  <h3 className="font-semibold mb-2">Clear Communication</h3>
                  <p className="text-gray-300 text-sm">
                    We explain all costs and scope before starting your project
                  </p>
                </div>
              </div>
              <p className="text-gray-300 text-sm max-w-2xl mx-auto">
                Landscaping costs depend on project size, materials selected, and specific requirements. We provide a detailed written estimate during your free consultation so you know exactly what to expect.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="py-20 md:py-28 bg-white" data-testid="about-section">
        <div className="container mx-auto px-4 md:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="inline-block text-[#2E5C31] font-semibold text-sm uppercase tracking-wider mb-3">About Us</span>
              <h2 className="text-3xl md:text-4xl font-bold text-[#1A1A1A] mb-6 font-['Poppins']">
                Seattle's Trusted Landscaping Experts
              </h2>
              <p className="text-[#4A4A4A] mb-6 leading-relaxed">
                DecorVista is a locally owned and operated landscaping company serving residential homeowners throughout Seattle and the surrounding areas. Our experienced team is dedicated to transforming outdoor spaces with quality workmanship and attention to detail.
              </p>
              <p className="text-[#4A4A4A] mb-8 leading-relaxed">
                We understand the unique climate and conditions of the Pacific Northwest, allowing us to recommend the best plants, materials, and solutions for your property.
              </p>
              
              <div className="bg-[#F2F5F3] rounded-xl p-6 mb-8">
                <h3 className="font-semibold text-[#1A1A1A] mb-4 font-['Poppins']">Contact Information</h3>
                <div className="space-y-3 text-[#4A4A4A]">
                  <p><strong>Business Name:</strong> DecorVista Landscaping Services</p>
                  <p><strong>Address:</strong> 667 Harbor View Drive, Seattle, WA 98101</p>
                  <p><strong>Phone:</strong> <a href="tel:+18888808889" className="text-[#2E5C31] hover:underline">+1-888-880-8889</a></p>
                  <p><strong>Email:</strong> <a href="mailto:support@decorvista.store" className="text-[#2E5C31] hover:underline">support@decorvista.store</a></p>
                  <p><strong>Hours:</strong> Mon - Sat: 9:00 AM - 6:00 PM | Sunday: Closed</p>
                  <p><strong>Service Area:</strong> Seattle, Bellevue, Kirkland, Redmond, and surrounding areas</p>
                </div>
              </div>

              <Link to="/about">
                <Button
                  variant="outline"
                  className="border-[#2E5C31] text-[#2E5C31] hover:bg-[#2E5C31] hover:text-white rounded-full px-8 py-3 font-semibold transition-all duration-300"
                  data-testid="learn-more-btn"
                >
                  Learn More About Us
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
            </div>
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1558904541-efa843a96f01?q=80&w=800&auto=format&fit=crop"
                alt="DecorVista Landscaping Team"
                className="rounded-2xl shadow-xl"
              />
              <div className="absolute -bottom-6 -left-6 bg-[#2E5C31] text-white p-6 rounded-xl shadow-lg hidden md:block">
                <p className="text-3xl font-bold font-['Poppins']">10+</p>
                <p className="text-sm">Years Experience</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Lead Capture Section */}
      <section className="py-20 md:py-28 bg-[#F2F5F3]" data-testid="lead-capture-section">
        <div className="container mx-auto px-4 md:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            <div className="lg:sticky lg:top-32">
              <span className="inline-block text-[#2E5C31] font-semibold text-sm uppercase tracking-wider mb-3">Get Started</span>
              <h2 className="text-3xl md:text-4xl font-bold text-[#1A1A1A] mb-6 font-['Poppins']">
                Ready to Transform Your Outdoor Space?
              </h2>
              <p className="text-[#4A4A4A] mb-8 leading-relaxed">
                Fill out the form to request your free, no-obligation estimate. Our team will get back to you within 24 hours to schedule your consultation.
              </p>
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-[#2E5C31]" />
                  <span className="text-[#4A4A4A]">Free on-site consultation</span>
                </div>
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-[#2E5C31]" />
                  <span className="text-[#4A4A4A]">Detailed written estimate</span>
                </div>
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-[#2E5C31]" />
                  <span className="text-[#4A4A4A]">No pressure, no obligation</span>
                </div>
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-[#2E5C31]" />
                  <span className="text-[#4A4A4A]">Response within 24 hours</span>
                </div>
              </div>
            </div>
            <LeadForm />
          </div>
        </div>
      </section>

      {/* Floating CTA (Mobile) */}
      <div className="fixed bottom-6 left-1/2 transform -translate-x-1/2 z-50 md:hidden">
        <Link to="/contact#estimate-form">
          <Button
            className="bg-[#2E5C31] hover:bg-[#244A26] text-white rounded-full px-6 py-3 font-semibold shadow-xl"
            data-testid="floating-cta-btn"
          >
            Get Free Estimate
          </Button>
        </Link>
      </div>
    </div>
  );
};

export default HomePage;
